import { configureRefreshFetch, fetchJSON } from 'refresh-fetch';
import { getLoginContext } from './Auth';
import fetch from "cross-fetch";
import merge from 'lodash/merge';
import { useAuth } from './AuthContext';
import { getCurrentUser, setCurrentUser, userTokens } from '../utils/Hooks/useLocalStorage';
import 'isomorphic-unfetch';

const retrieveToken = () => {
    const user = getCurrentUser() as userTokens | { accessToken: string, refreshTokens: string[] };
    if (user && user.accessToken) {
        if (typeof user.accessToken === "string") return user.accessToken;
        else return user.accessToken.token;
    }
    return "";
}
const saveToken = (token: { accessToken: { token: string, expiresIn: number }; refreshToken: string; }, i: number) => {
    const user = getCurrentUser()!;
    user.accessToken = token.accessToken;
    user!.refreshTokens[i] = token.refreshToken;
    user!.newToken = true;
    const success = setCurrentUser(user!);
    if (!success) {
        console.error("Failed to persist token; login will fail on next page load");
        throw new Error("Token storage quota exceeded");
    }
}

export const fetchJSONWithToken = async (url: string, options?: RequestInit, refresh = false) => {
    const token = retrieveToken()
    let optionsWithToken = options;
    if (token != null) {
        optionsWithToken = merge({}, options, {
            headers: {
                Authorization: `Bearer ${token}`
            }
        })
    }

    return await fetchJSON(url, optionsWithToken);
}

const shouldRefreshToken = (error?: { response: { status: number; }; body: string; }) => {
    return error?.response?.status === 401 && error.body === '';

}
const refreshToken = async () => {
    let user = getCurrentUser()!;
    let oldToken = user.accessToken.token;
    let i = 0;
    if (user) for (; i < user.refreshTokens.length; i++) {
        const safeUser = user;
        try {
            const data = JSON.stringify({
                accessToken: safeUser.accessToken.token ?? safeUser.accessToken,
                refreshToken: safeUser.refreshTokens[i]
            });

            const response = await fetch('/auth/refreshtoken', {
                headers: { "Content-Type": "application/json", Authorization: `Bearer ${safeUser.accessToken.token}` },
                method: 'POST',
                body: data,
            }).catch(e => {
                if (e.response) return e;
                else throw e;
            }) as Response;
            if (response.ok) {
                const body = await response.json() as {
                    accessToken: { expiresIn: number, token: string },
                    refreshToken: string
                };
                // Replace the access token and the used refresh token (keep the rest)
                saveToken({ accessToken: body.accessToken, refreshToken: body.refreshToken }, i);
                break;
            }
            else {
                const safeI = i;
                safeUser.refreshTokens = (safeUser.refreshTokens as string[]).filter((_, keyindex) => safeI !== keyindex);
                const success = setCurrentUser(safeUser);
                if (!success) {
                    console.warn("Failed to persist removed refresh token; token rotation may be incomplete");
                }
            }
        }
        catch (e) {
            console.error(e);
        }
    }
    user = getCurrentUser()!;
    // If the token was changed
    if (oldToken !== user.accessToken.token) {
        // Write that there was a change to localstorage
        const success = setCurrentUser({ ...user, refreshed: true });
        if (!success) {
            console.warn("Failed to persist refreshed token state; next refresh will re-fetch");
        }
        // Ensure that there are at least 2 refresh tokens saved.
        if (user && (i > 0 || user.refreshTokens.length < 2)) {
            // Attempt to replenish tokens in the background
            fetchJSONWithToken('/auth/replenishtokens', {
                method: 'POST',
                body: JSON.stringify({
                    refreshTokens: user.refreshTokens,
                    accessToken: user.accessToken
                })
            }).catch(e => e).then((replenishment: { body?: { refreshTokens: string[] }, response: Response }) => {
                // If the replenishment was successful - set the new tokens
                if (user && replenishment.body?.refreshTokens) {
                    const success = setCurrentUser({ ...user, refreshTokens: replenishment.body!.refreshTokens });
                    if (!success) {
                        console.warn("Failed to persist new refresh tokens; token rotation may be incomplete");
                    }
                }
            });
        }
    }
    else {
        console.error("All keys failed")
        throw new Error("None of the refresh keys worked.");
    }
}
const fetchWithRefresh = configureRefreshFetch({
    fetch: fetchJSONWithToken,
    shouldRefreshToken,
    refreshToken,
});
const useRefreshToken = () => {
    const [, dispatchSetuser] = useAuth();
    const refresh = async () => {
        const v = await refreshToken();
        const user = getLoginContext(v);

        dispatchSetuser({
            type: "setAuthDetails", payload: user
        });
    }


    return refresh;
}

export { fetchWithRefresh as fetch, useRefreshToken };